import { useMemo, useReducer, } from "react";
let createReducer = (factory) => (state, action) => factory[action.type]?.(state, action) ?? state;
function mapDispatch(dispatch, factory) {
    return Object.fromEntries(Object.keys(factory).map((type) => [
        type,
        (payload) => dispatch({ type, payload }),
    ]));
}
/**
 * React hook for creating and managing a local React store based on a reducer factory.
 *
 * `useStore` initializes the store with the provided `initialState` and returns a tuple containing:
 * 1. The current state of the store.
 * 2. A dispatching object (mutations) that allows triggering actions to update the store.
 *
 * The reducer factory `factory` defines how state transitions are handled for different action types.
 * The returned state is derived from the reducer functions in the factory, and the mutations object
 * provides methods to dispatch actions that will trigger the corresponding reducer functions.
 *
 * @example
 * const factory = {
 *   increment: (state, action) => ({ ...state, count: state.count + action.payload }),
 *   decrement: (state, action) => ({ ...state, count: state.count - action.payload }),
 * };
 * const [state, mutations] = useStore(factory, { count: 0 });
 * // Now, mutations.increment(1) updates state.count by +1
 */
export function useStore(factory, initialState) {
    const [state, dispatch] = useReducer(useMemo(() => createReducer(factory), [factory]), initialState);
    return [
        state,
        useMemo(() => mapDispatch(dispatch, factory), [factory]),
    ];
}
